import React, { useEffect, useState } from 'react'
import { usePostHog } from '../../contexts/PostHogProvider'
import { useUserIdentityContext } from '../../contexts/UserIdentityContext'
import {
  Box,
  Typography,
  LinearProgress,
  Button,
  Card,
  CardContent,
  Stack,
  Chip,
  Alert
} from '@mui/material'
import { Cancel as CancelIcon, CheckCircle as CompleteIcon } from '@mui/icons-material'
import {
  JobStatusResponse,
  JobResultsResponse,
  JobStatus
} from '../../types/visual-semiotics'
import { config } from '../../config/environment'

interface VisualSemioticsProcessingProps {
  jobId: string
  onGetJobStatus: (jobId: string) => Promise<JobStatusResponse>
  onGetJobResults: (jobId: string) => Promise<JobResultsResponse>
  onCancelJob: (jobId: string) => Promise<void>
  onComplete: (results: JobResultsResponse) => void
  onError: (error: string) => void
  organization?: string
}

const VisualSemioticsProcessing: React.FC<VisualSemioticsProcessingProps> = ({
  jobId,
  onGetJobStatus,
  onGetJobResults,
  onCancelJob,
  onComplete,
  onError,
  organization
}) => {
  const { trackEvent } = usePostHog()
  const {department} = useUserIdentityContext()

  const [jobStatus, setJobStatus] = useState<JobStatusResponse | null>(null)
  const jobStatusRef = React.useRef<JobStatusResponse | null>(null)
  const [cancelling, setCancelling] = useState(false)
  const [eventSource, setEventSource] = useState<EventSource | null>(null)

  // Connect to SSE when jobId is available
  useEffect(() => {
    if (!jobId || eventSource) return

    // Fetch initial job status first
    onGetJobStatus(jobId).then(initialStatus => {
      setJobStatus(initialStatus)
      jobStatusRef.current = initialStatus
    }).catch(err => {
      console.error('Failed to fetch initial job status:', err)
    })

    const sse = new EventSource(`${config.API_BASE_URL}/api/image-sourcing/jobs/${jobId}/stream`, { withCredentials: true })

    sse.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)

        // Backend sends data directly (not wrapped in type/data structure)
        // Use ref to get latest values (avoids closure stale state)
        const currentStatus = jobStatusRef.current
        const statusUpdate: JobStatusResponse = {
          jobId: data.jobId.toString(),
          status: data.status,
          emotion: currentStatus?.emotion || '',
          provider: currentStatus?.provider || '',
          quantity: currentStatus?.quantity || 0,
          progress: data.progress,
          currentActivity: data.currentActivity,
          createdAt: currentStatus?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          statistics: {
            totalFound: data.statistics.totalFound,
            totalFiltered: data.statistics.totalFiltered,
            totalSaved: data.statistics.totalSaved,
            totalDownloaded: data.statistics.totalSaved // Use totalSaved as totalDownloaded
          }
        }

        setJobStatus(statusUpdate)
        jobStatusRef.current = statusUpdate

        // Handle completion
        if (['completed', 'failed', 'cancelled'].includes(statusUpdate.status)) {
          if (statusUpdate.status === 'completed') {
            console.log('[VisualSemioticsProcessing] Job completed, fetching results...')
            trackEvent('vs_sourcing_completed', {
              provider: statusUpdate.provider,
              emotion: statusUpdate.emotion,
              images_sourced: statusUpdate.statistics?.totalSaved ?? 0,
              images_filtered: statusUpdate.statistics?.totalFiltered ?? 0,
              images_found: statusUpdate.statistics?.totalFound ?? 0,
              organization,
              user_department: department,
            })
            // Fetch results when complete
            onGetJobResults(jobId)
              .then(results => {
                console.log('[VisualSemioticsProcessing] Results fetched:', results)
                console.log('[VisualSemioticsProcessing] Calling onComplete with results')
                onComplete(results)
                sse.close()
                setEventSource(null)
              })
              .catch(err => {
                console.error('[VisualSemioticsProcessing] Failed to fetch results:', err)
                trackEvent('vs_error', {
                  error_type: 'sourcing',
                  error_message: err instanceof Error ? err.message : 'Failed to fetch job results',
                  organization,
                  user_department: department,
                })
                onError('Failed to fetch job results')
                sse.close()
                setEventSource(null)
              })
          } else if (statusUpdate.status === 'failed') {
            trackEvent('vs_error', {
              error_type: 'sourcing',
              organization,
              user_department: department,
            })
            onError('Job failed to complete')
            sse.close()
            setEventSource(null)
          } else if (statusUpdate.status === 'cancelled') {
            sse.close()
            setEventSource(null)
          }
        }
      } catch (error) {
        console.error('Error parsing SSE data:', error)
        trackEvent('vs_error', {
          error_type: 'sourcing',
          error_message: error instanceof Error ? error.message : 'Failed to process job update',
          organization,
          user_department: department,
        })
        onError('Failed to process job update')
      }
    }

    sse.onerror = () => {
      console.error('SSE connection error, falling back to polling')
      trackEvent('vs_error', {
        error_type: 'sourcing',
        organization,
        user_department: department,
      })
      sse.close()
      setEventSource(null)
      // Fallback to polling
      startPolling()
    }
    
    setEventSource(sse)
    
    return () => {
      if (sse.readyState !== EventSource.CLOSED) {
        sse.close()
      }
      setEventSource(null)
    }
  }, [jobId, onGetJobResults, onComplete, onError])

  // Fallback polling function
  const startPolling = () => {
    const pollStatus = async () => {
      try {
        const status = await onGetJobStatus(jobId)
        setJobStatus(status)
        jobStatusRef.current = status

        if (['completed', 'failed', 'cancelled'].includes(status.status)) {
          if (status.status === 'completed') {
            try {
              const results = await onGetJobResults(jobId)
              onComplete(results)
            } catch (err) {
              trackEvent('vs_error', {
                error_type: 'sourcing',
                organization,
                user_department: department,
              })
              onError('Failed to fetch job results')
            }
          } else if (status.status === 'failed') {
            trackEvent('vs_error', {
              error_type: 'sourcing',
              organization,
              user_department: department,
            })
            onError('Job failed to complete')
          }
          return false // Stop polling
        }
        return true // Continue polling
      } catch (err) {
        trackEvent('vs_error', {
          error_type: 'sourcing',
          organization,
          user_department: department,
        })
        onError('Failed to get job status')
        return false // Stop polling
      }
    }

    // Initial status check
    pollStatus().then(shouldContinue => {
      if (shouldContinue) {
        // Set up polling interval
        const interval = setInterval(async () => {
          const shouldContinue = await pollStatus()
          if (!shouldContinue) {
            clearInterval(interval)
          }
        }, 2000)
      }
    })
  }


  const handleCancel = async () => {
    try {
      setCancelling(true)
      await onCancelJob(jobId)
      
      // Close SSE connection
      if (eventSource) {
        eventSource.close()
        setEventSource(null)
      }
    } catch (err) {
      trackEvent('vs_error', {
        error_type: 'sourcing',
        organization,
        user_department: department,
      })
      onError('Failed to cancel job')
    } finally {
      setCancelling(false)
    }
  }

  const getStatusColor = (status: JobStatus) => {
    switch (status) {
      case 'running': return 'primary'
      case 'completed': return 'success'
      case 'failed': return 'error'
      case 'cancelled': return 'default'
      default: return 'default'
    }
  }

  const getStatusIcon = (status: JobStatus) => {
    switch (status) {
      case 'completed': return <CompleteIcon color="success" />
      default: return undefined
    }
  }

  if (!jobStatus) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" gutterBottom>
          Initializing...
        </Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    )
  }

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto' }}>
      <Typography variant="h5" gutterBottom>
        Processing Images
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="h6">
              Job Status
            </Typography>
            <Chip 
              label={jobStatus.status.toUpperCase()} 
              color={getStatusColor(jobStatus.status)}
              icon={getStatusIcon(jobStatus.status)}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            {jobStatus.currentActivity}
          </Typography>

          <LinearProgress 
            variant="determinate" 
            value={jobStatus.progress} 
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" sx={{ mb: 2 }}>
            {jobStatus.progress}% complete
            {jobStatus.estimatedTimeRemaining && (
              ` • ${Math.ceil(jobStatus.estimatedTimeRemaining / 1000)}s remaining`
            )}
          </Typography>

          <Stack direction="row" spacing={3}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Found
              </Typography>
              <Typography variant="h6">
                {jobStatus.statistics.totalFound}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Downloaded
              </Typography>
              <Typography variant="h6">
                {jobStatus.statistics.totalDownloaded}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Filtered
              </Typography>
              <Typography variant="h6">
                {jobStatus.statistics.totalFiltered}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {jobStatus.status === 'running' && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CancelIcon />}
            onClick={handleCancel}
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling...' : 'Cancel Job'}
          </Button>
        </Box>
      )}

      {jobStatus.status === 'failed' && (
        <Alert severity="error">
          The image sourcing job failed to complete. Please try again.
        </Alert>
      )}

      {jobStatus.status === 'cancelled' && (
        <Alert severity="warning">
          The image sourcing job was cancelled.
        </Alert>
      )}
    </Box>
  )
}

export default VisualSemioticsProcessing